import imageCompression from 'browser-image-compression';
import Cropper from 'cropperjs';
import { createLogger } from '@/lib/logger';

const logger = createLogger('lib/services/photo.service');

export interface PhotoProcessingOptions {
  maxWidth?: number;
maxHeight?: number;
  quality?: number;
  aspectRatio?: number;
}

export interface ProcessedPhoto {
  blob: Blob;
  url: string;
}

export class PhotoService {
  /**
   * Process and compress a photo before upload
   */
  static async processPhoto(file: File, options: PhotoProcessingOptions = {}): Promise<ProcessedPhoto> {
    const {
      maxWidth = 800,
      maxHeight = 800,
      quality = 0.8,
    } = options;

    try {
      // Compress the image
      const compressedFile = await imageCompression(file, {
        maxWidthOrHeight: Math.max(maxWidth, maxHeight),
        initialQuality: quality,
        useWebWorker: true,
      });

      // Convert to blob
      const blob = new Blob([compressedFile], { type: 'image/jpeg' });
      const url = URL.createObjectURL(blob);

      return { blob, url };
    } catch (error) {
      logger.error('[PhotoService] Error processing photo:', error);
      throw new Error('Failed to process photo');
    }
  }

  /**
   * Create a cropper instance
   */
  static createCropper(
    imageElement: HTMLImageElement,
    options: PhotoProcessingOptions = {}
  ): Cropper {
    const {
      aspectRatio = 1
    } = options;

    return new Cropper(imageElement, {
      aspectRatio,
      viewMode: 1,
      dragMode: 'move',
      background: false,
      responsive: true,
      autoCropArea: 1,
      checkOrientation: false,
      cropBoxMovable: true,
      cropBoxResizable: true,
      toggleDragModeOnDblclick: false,
    });
  }

  /**
   * Get cropped canvas from cropper instance
   */
  static getCroppedCanvas(
    cropper: Cropper,
    options: PhotoProcessingOptions = {}
  ): HTMLCanvasElement {
    const {
      maxWidth = 800,
      maxHeight = 800,
    } = options;

    return cropper.getCroppedCanvas({
      width: maxWidth,
      height: maxHeight,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
    });
  }

  /**
   * Convert canvas to blob
   */
  static async canvasToBlob(
    canvas: HTMLCanvasElement,
    quality: number = 0.8
  ): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => {
          if (blob) {
            // Ensure we have a valid blob with the correct MIME type
            const validBlob = new Blob([blob], { type: 'image/jpeg' });
            resolve(validBlob);
          } else {
            reject(new Error('Failed to create blob from canvas'));
          }
        },
        'image/jpeg',
        quality
      );
    });
  }

  /**
   * Clean up object URLs to prevent memory leaks
   */
  static revokeObjectUrl(url: string): void {
    if (url.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }
} 